import { type AxiosError } from 'axios'
import { useMutation, type UseMutationOptions } from 'react-query'

import agent from 'agent'
import { oauthMutationKeys } from 'queries/oauth/query-keys'

import type {
  OAuthConsentDecision,
  OAuthConsentResult
} from 'types/api/oauth/consent'

function confirmOAuthConsent(requestId: string) {
  return async (decision: OAuthConsentDecision) => {
    const [endpoint] = oauthMutationKeys.confirmConsent(requestId)

    const response = await agent.post<OAuthConsentResult>(endpoint, decision, {
      // Required, and the one place in the app where it is. The API binds this confirmation to the
      // browser that began the authorization request, using an HttpOnly `oauth2_authorize_state`
      // cookie set on the API origin by GET /oauth2/authorize. Ordinary calls authenticate with the
      // Authorization header alone and need no cookies; this one is refused with "Authorization
      // state mismatch" unless the cookie rides along, so the request must be credentialed.
      withCredentials: true
    })

    return response.data
  }
}

/**
 * Records the user's approve/deny decision.
 *
 * Returns a `redirectTo` URL rather than following a redirect: the API answers an XHR from a
 * different origin and cannot 302 us to the client's callback, so the caller navigates.
 */
export function useConfirmOAuthConsent(
  requestId: string,
  options?: UseMutationOptions<
    OAuthConsentResult,
    AxiosError,
    OAuthConsentDecision
  >
) {
  return useMutation<OAuthConsentResult, AxiosError, OAuthConsentDecision>({
    ...options,
    mutationFn: confirmOAuthConsent(requestId),
    mutationKey: oauthMutationKeys.confirmConsent(requestId)
  })
}
